import format from 'date-fns/format';

export const officeHours = [
  ['Monday - Friday', '8:00 AM - 8:00 PM'],
  ['Saturday', '10:00 AM - 4:00 PM'],
  ['Sunday', 'Closed'],
  ['Bank Holiday', '9:00 AM - 6:00 PM']
];

export const christmasHours = [
  ['Saturday 23rd December', '10:00 AM - 4:00 PM'],
  ['Sunday 24th December', 'Closed'],
  ['Monday 25th December', 'Closed'],
  ['Tuesday 26th December', 'Closed'],
  ['Wednesday 27th December', 'Closed'],
  ['Thursday 28th December', 'Closed'],
  ['Friday 29th December', 'Closed'],
  ['Saturday 30th December', 'Closed'],
  ['Sunday 31st December', 'Closed'],
  ['Monday 1st January', 'Closed'],
  ['Tuesday 2nd January', 'Closed'],
  ['Wednesday 3rd January', '9:00 AM - 7:00 PM']
];

const DATE_FORMAT_STRING = 'yyyy-MM-dd';

export const bankHolidays = [
  '2021-12-25',
  '2021-12-26',
  '2021-12-27',
  '2021-12-28',
  '2021-12-29',
  '2021-12-30',
  '2021-12-31',
  '2022-01-01',
  '2022-01-02',
  '2022-01-03',
  '2022-04-15',
  '2022-04-18',
  '2022-05-02',
  '2022-06-02',
  '2022-06-03',
  '2022-08-29',
  '2022-12-26',
  '2022-12-27',
  '2023-01-02',
  '2023-04-07', // Good Friday
  '2023-04-10', // Easter Monday
  '2023-05-01', // Early May bank holiday
  '2023-05-08', // Bank holiday for the coronation of King Charles III
  '2023-05-29', // Spring bank holiday
  '2023-08-28', // Summer bank holiday
  '2023-12-24', // office closure for christmas
  '2023-12-25',
  '2023-12-26',
  '2023-12-27', // office closure for christmas
  '2023-12-28', // office closure for christmas
  '2023-12-29', // office closure for christmas
  '2023-12-30', // office closure for christmas
  // 2024
  '2024-01-01',
  '2024-03-29', // Good Friday
  '2024-04-01', // Easter Monday
  '2024-05-06', // Early May bank holiday
  '2024-05-27', // Spring bank holiday
  '2024-08-26', // Summer bank holiday
  '2024-12-25',
  '2024-12-26',
  // 2025
  '2025-01-01',
  '2025-04-18', // Good Friday
  '2025-04-21', // Easter Monday
  '2025-05-05', // Early May bank holiday
  '2025-05-26', // Spring bank holiday
  '2025-08-25', // Summer bank holiday
  '2025-12-25',
  '2025-12-26'
];

// apart from bank holidays, we also have office closed dates, such as Christmas
// should be in the format 'yyyy-MM-dd'
export const officeClosedDates: string[] = [];

export const viewingHours = {
  sunday: {
    office: [0, 0],
    viewingAppointments: {
      morning: [0, 0],
      lunch: [0, 0],
      afternoon: [0, 0],
      evening: [0, 0]
    }
  },
  monday: {
    office: [9, 18],
    viewingAppointments: {
      morning: [9, 12],
      lunch: [12, 14],
      afternoon: [16, 18],
      evening: [0, 0]
    }
  },
  tuesday: {
    office: [9, 19],
    viewingAppointments: {
      morning: [9, 12],
      lunch: [12, 14],
      afternoon: [16, 19],
      evening: [0, 0]
    }
  },
  wednesday: {
    office: [9, 19],
    viewingAppointments: {
      morning: [9, 12],
      lunch: [12, 14],
      afternoon: [16, 19],
      evening: [0, 0]
    }
  },
  thursday: {
    office: [9, 19],
    viewingAppointments: {
      morning: [9, 12],
      lunch: [12, 14],
      afternoon: [16, 19],
      evening: [0, 0]
    }
  },
  friday: {
    office: [9, 18],
    viewingAppointments: {
      morning: [9, 12],
      lunch: [12, 14],
      afternoon: [16, 18],
      evening: [0, 0]
    }
  },
  saturday: {
    office: [10, 16],
    viewingAppointments: {
      morning: [10, 12],
      lunch: [12, 14],
      afternoon: [14, 16],
      evening: [0, 0]
    }
  },
  bankHoliday: {
    office: [9, 18],
    viewingAppointments: {
      morning: [9, 12],
      lunch: [12, 14],
      afternoon: [16, 18],
      evening: [0, 0]
    }
  }
};

export const OfficeHoursUtils = {
  isBankHoliday(date: Date): boolean {
    return bankHolidays.indexOf(date.toISOString().substr(0, 10)) >= 0;
  },
  isInBusinessHoursNow(closedDates = officeClosedDates): boolean {
    const now = new Date();

    if (now > new Date(bankHolidays[bankHolidays.length - 1])) {
      // add a reminder to update this, that we will see in the error logs
      // eslint-disable-next-line no-console
      console.error('Reminder: fill in bank holidays for next year');
    }

    // always show closed when it is in closedDates
    const isClosed = closedDates.includes(format(now, DATE_FORMAT_STRING));

    if (isClosed) {
      return false;
    }

    // Now check the actual opening times
    const day = now.getDay();
    const startingTime = new Date();
    const endingTime = new Date();
    const days = [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday'
    ];

    const todayOfficeHours = this.isBankHoliday(now)
      ? viewingHours.bankHoliday.office
      : viewingHours[days[day]].office;

    startingTime.setHours(todayOfficeHours[0], 0, 0);
    endingTime.setHours(todayOfficeHours[1], 0, 0);

    if (now >= startingTime && now <= endingTime) {
      return true;
    }

    return false;
  },
  getViewingHours(date: Date, closedDates = officeClosedDates): number[][] {
    if (date.toString() === 'Invalid Date') {
      return [];
    }

    const isClosed = closedDates.includes(format(date, DATE_FORMAT_STRING));

    if (isClosed) {
      return [];
    }

    const dayAppointmentHours = this.isBankHoliday(date)
      ? viewingHours.bankHoliday.viewingAppointments
      : Object.values(viewingHours)[date.getDay()].viewingAppointments;

    return Object.values(dayAppointmentHours).filter(
      (times) => times[0] !== 0 && times[1] !== 0
    );
  }
};
